/**
 * Skeleton loader for ChallengeDetailScreen
 */
import * as React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container, Row, Col } from 'reactstrap';
import { Theme } from '../../Theme/Theme';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const PageContainer = styled(Container)`
  padding-top: 24px;
  padding-bottom: 48px;
`;

const ContentRow = styled(Row)`
  margin-top: 32px;
`;

const MainContent = styled(Col)`
  margin-bottom: 24px;
`;

const SidebarContent = styled(Col)`
  @media (min-width: 992px) {
    position: sticky;
    top: 80px;
    align-self: flex-start;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '14px'};
`;

const SkeletonHeader = styled.div`
  position: relative;
`;

const SkeletonHeaderImage = styled(SkeletonBase)`
  height: 200px;
  border-radius: 16px;

  @media (min-width: 768px) {
    height: 280px;
  }
`;

const SkeletonHeaderContent = styled.div`
  padding: 20px 0;
`;

const SkeletonBadge = styled(SkeletonBase)`
  width: 80px;
  height: 24px;
  border-radius: 12px;
  margin-bottom: 12px;
`;

const SkeletonAppBanner = styled(SkeletonBase)`
  height: 160px;
  border-radius: 16px;
  margin-top: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const SkeletonActionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SkeletonStatsCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const SkeletonStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SkeletonStatItem = styled.div`
  text-align: center;
  padding: 12px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 12px;
`;

const SkeletonMapContainer = styled(SkeletonBase)`
  height: 300px;
  border-radius: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    height: 400px;
  }
`;

const SkeletonLocationCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const SkeletonLocationImage = styled(SkeletonBase)`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  flex-shrink: 0;
`;

const SkeletonLeaderboardCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const SkeletonLeaderboardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${Theme.Colors.lightestGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const SkeletonCircle = styled(SkeletonBase)<{ size?: string }>`
  width: ${({ size }) => size || '40px'};
  height: ${({ size }) => size || '40px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 44px;
  width: 140px;
  border-radius: 8px;
`;

export const ChallengeDetailSkeleton: React.FC = () => (
  <PageContainer>
    {/* Header Skeleton */}
    <SkeletonHeader>
      <SkeletonHeaderImage />
      <SkeletonHeaderContent>
        <SkeletonBadge />
        <SkeletonText width="70%" height="28px" style={{ marginBottom: 12 }} />
        <SkeletonText width="100%" height="14px" style={{ marginBottom: 6 }} />
        <SkeletonText width="85%" height="14px" />
      </SkeletonHeaderContent>
    </SkeletonHeader>

    {/* App Banner Skeleton */}
    <SkeletonAppBanner />

    {/* Action Card Skeleton */}
    <SkeletonActionCard>
      <div style={{ flex: 1 }}>
        <SkeletonText width="60%" height="18px" style={{ marginBottom: 8 }} />
        <SkeletonText width="80%" height="14px" />
      </div>
      <SkeletonButton />
    </SkeletonActionCard>

    {/* Content Row */}
    <ContentRow>
      <MainContent xs={12} lg={8}>
        {/* Stats Card Skeleton */}
        <SkeletonStatsCard>
          <SkeletonText width="120px" height="18px" />
          <SkeletonStatsGrid>
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonStatItem key={i}>
                <SkeletonText width="50%" height="24px" style={{ margin: '0 auto 8px' }} />
                <SkeletonText width="70%" height="12px" style={{ margin: '0 auto' }} />
              </SkeletonStatItem>
            ))}
          </SkeletonStatsGrid>
        </SkeletonStatsCard>

        {/* Map Skeleton */}
        <SkeletonMapContainer />

        {/* Locations Skeleton */}
        <SkeletonStatsCard>
          <SkeletonText width="100px" height="18px" style={{ marginBottom: 16 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonLocationCard key={i}>
              <SkeletonLocationImage />
              <div style={{ flex: 1 }}>
                <SkeletonText width="70%" height="15px" style={{ marginBottom: 6 }} />
                <SkeletonText width="50%" height="12px" />
              </div>
              <SkeletonText width="60px" height="24px" />
            </SkeletonLocationCard>
          ))}
        </SkeletonStatsCard>
      </MainContent>

      <SidebarContent xs={12} lg={4}>
        {/* Leaderboard Skeleton */}
        <SkeletonLeaderboardCard>
          <SkeletonText width="120px" height="18px" style={{ marginBottom: 16 }} />
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonLeaderboardItem key={i}>
              <SkeletonText width="24px" height="24px" style={{ borderRadius: '50%' }} />
              <SkeletonCircle size="36px" />
              <div style={{ flex: 1 }}>
                <SkeletonText width="60%" height="14px" style={{ marginBottom: 4 }} />
                <SkeletonText width="40%" height="12px" />
              </div>
              <SkeletonText width="50px" height="16px" />
            </SkeletonLeaderboardItem>
          ))}
        </SkeletonLeaderboardCard>
      </SidebarContent>
    </ContentRow>
  </PageContainer>
);
